/**
 * Velora for Android (apps/android): the page and the app talk over one
 * WebMessageListener port, `window.VeloraAndroid`, that the app injects only
 * into the workspace origin's main frame. Absent in every browser.
 *
 * Messages are JSON strings, and only ids, paths and status words cross:
 *
 *   page -> app  {type:'hello'}                   the page is ready; app answers 'info'
 *                {type:'session', signedIn, signOut?}  drives the app lock; signOut (an
 *                                                  explicit sign-out) also stops pushes
 *                {type:'push.register'}            asks for notification permission + token
 *   app -> page  {type:'info', version, pushConfigured, lockAvailable, tokenChanged}
 *                {type:'navigate', path}           a deep link or a notification tap
 *                {type:'push', status, token?}     'ok' | 'denied' | 'unavailable' | 'error'
 *
 * The FCM token goes from here to Velora's own server (identity
 * me/mobile-devices) and nowhere else.
 */

export interface AndroidPort {
  postMessage(message: string): void;
  onmessage: ((event: { data: unknown }) => void) | null;
  addEventListener?: (type: 'message', listener: (event: { data: unknown }) => void) => void;
}

export interface AndroidInfo {
  version: string | null;
  pushConfigured: boolean;
  lockAvailable: boolean;
  tokenChanged: boolean;
}

export type PushStatus = 'ok' | 'denied' | 'unavailable' | 'error';
export interface PushAnswer {
  status: PushStatus;
  token: string | null;
}

export type AndroidMessage =
  | { type: 'info'; info: AndroidInfo }
  | { type: 'navigate'; path: string }
  | { type: 'push'; push: PushAnswer };

const UA_MARK = /\bVeloraAndroid\/(\d+\.\d+\.\d+)/;

/** The app's port, only inside Velora for Android. */
export function androidPort(): AndroidPort | null {
  if (typeof window === 'undefined') return null;
  const port = (window as { VeloraAndroid?: AndroidPort }).VeloraAndroid;
  if (!port || typeof port.postMessage !== 'function') return null;
  if (typeof navigator !== 'undefined' && !UA_MARK.test(navigator.userAgent)) return null;
  return port;
}

export function isAndroidApp(): boolean {
  return androidPort() !== null;
}

/** "0.1.0" from the app's user agent, or null. */
export function androidAppVersion(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): string | null {
  return UA_MARK.exec(ua)?.[1] ?? null;
}

/** A same-origin workspace path (the app checked it too). */
export function isSafeInAppPath(path: unknown): path is string {
  return (
    typeof path === 'string' &&
    path.length <= 1024 &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.includes('\\') &&
    // eslint-disable-next-line no-control-regex
    !/[\u0000-\u001f\u007f]/.test(path)
  );
}

/** Parses one message from the app; anything unexpected is dropped. */
export function parseAndroidMessage(raw: unknown): AndroidMessage | null {
  if (typeof raw !== 'string' || raw.length > 8192) return null;
  let m: Record<string, unknown>;
  try {
    const v: unknown = JSON.parse(raw);
    if (!v || typeof v !== 'object') return null;
    m = v as Record<string, unknown>;
  } catch {
    return null;
  }
  switch (m['type']) {
    case 'info':
      return {
        type: 'info',
        info: {
          version: typeof m['version'] === 'string' ? m['version'] : null,
          pushConfigured: m['pushConfigured'] === true,
          lockAvailable: m['lockAvailable'] === true,
          tokenChanged: m['tokenChanged'] === true,
        },
      };
    case 'navigate':
      return isSafeInAppPath(m['path']) ? { type: 'navigate', path: m['path'] } : null;
    case 'push': {
      const s = m['status'];
      if (s !== 'ok' && s !== 'denied' && s !== 'unavailable' && s !== 'error') return null;
      const token = typeof m['token'] === 'string' && m['token'].length <= 4096 ? m['token'] : null;
      return { type: 'push', push: { status: s, token: s === 'ok' ? token : null } };
    }
    default:
      return null;
  }
}

/* ------------------------------------------------------------------------ */
/* One connection per page                                                   */
/* ------------------------------------------------------------------------ */

type Listener = (m: AndroidMessage) => void;
const listeners = new Set<Listener>();
let connected: AndroidPort | null = null;
let lastInfo: AndroidInfo | null = null;
let pendingNavigate: string | null = null;

function connect(): AndroidPort | null {
  if (connected) return connected;
  const port = androidPort();
  if (!port) return null;
  const onMessage = (event: { data: unknown }) => {
    const m = parseAndroidMessage(event.data);
    if (!m) return;
    if (m.type === 'info') lastInfo = m.info;
    if (m.type === 'navigate' && ![...listeners].length) pendingNavigate = m.path;
    for (const l of listeners) l(m);
  };
  if (typeof port.addEventListener === 'function') port.addEventListener('message', onMessage);
  else port.onmessage = onMessage;
  connected = port;
  port.postMessage(JSON.stringify({ type: 'hello' }));
  return port;
}

function post(message: Record<string, unknown>): void {
  connect()?.postMessage(JSON.stringify(message));
}

/** Subscribes to the app's messages (connects on first use). */
export function onAndroidMessage(listener: Listener): () => void {
  if (!connect()) return () => undefined;
  listeners.add(listener);
  if (pendingNavigate) {
    const p = pendingNavigate;
    pendingNavigate = null;
    listener({ type: 'navigate', path: p });
  }
  return () => listeners.delete(listener);
}

export function androidInfo(): AndroidInfo | null {
  return lastInfo;
}

/**
 * `signOut`: the person signed out (pushes stop on this phone). Without it, a
 * session that simply ended keeps the phone registered for category pushes.
 */
export function tellAndroidSession(signedIn: boolean, signOut = false): void {
  post({ type: 'session', signedIn, ...(signOut ? { signOut: true } : {}) });
}

/** Asks the app for its push token (it asks the person for permission first). */
export function requestAndroidPush(timeoutMs = 60_000): Promise<PushAnswer> {
  return new Promise((resolve) => {
    if (!connect()) {
      resolve({ status: 'unavailable', token: null });
      return;
    }
    let off = () => undefined as unknown;
    const timer = setTimeout(() => {
      off();
      resolve({ status: 'error', token: null });
    }, timeoutMs);
    off = onAndroidMessage((m) => {
      if (m.type !== 'push') return;
      clearTimeout(timer);
      off();
      resolve(m.push);
    });
    post({ type: 'push.register' });
  });
}

/** For tests. */
export function resetAndroidBridge(): void {
  listeners.clear();
  connected = null;
  lastInfo = null;
  pendingNavigate = null;
}
